import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import Modal from "@/components/Modal";
import { resolveImageUrl } from "@/components/ImageInput";
import { Trophy, DownloadSimple } from "@phosphor-icons/react";
import html2canvas from "html2canvas";
import { toast } from "sonner";

/**
 * Shared team-detail modal used by admin and public views.
 * Requires the `/teams/:id` endpoint (auth) OR pass a pre-loaded `teamData` prop
 * for public read-only usage (from /public/auctions/:id which returns teams+players).
 */
export default function TeamDetailModal({ teamId, open, onClose, publicData, brandLogoUrl }) {
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!open || !teamId) return;
    if (publicData) {
      setTeam(publicData);
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get(`/teams/${teamId}`)
      .then(({ data }) => setTeam(data))
      .catch(() => setTeam(null))
      .finally(() => setLoading(false));
  }, [open, teamId, publicData]);

  const downloadPng = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      // Preload every img inside the card via our backend proxy → base64 data URL.
      // This bypasses cross-origin (Google Drive etc.) tainting for html2canvas.
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const imgs = Array.from(cardRef.current.querySelectorAll("img"));
      const originals = imgs.map((el) => el.src);
      const toDataUrl = async (url) => {
        try {
          const isPlaceholder = url && url.startsWith(window.location.origin);
          const target = isPlaceholder ? url : `${backend}/api/proxy-image?url=${encodeURIComponent(url)}`;
          const r = await fetch(target);
          const blob = await r.blob();
          return await new Promise((res) => {
            const reader = new FileReader();
            reader.onloadend = () => res(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          return url;
        }
      };
      const dataUrls = await Promise.all(originals.map(toDataUrl));
      imgs.forEach((el, i) => { el.src = dataUrls[i]; });
      // Give the DOM one tick to apply the new src
      await new Promise((r) => setTimeout(r, 400));
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#F8FAFC",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      // Restore original srcs
      imgs.forEach((el, i) => { el.src = originals[i]; });
      const url = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `${(team?.short_name || team?.name || "squad").replace(/\s+/g, "-")}-squad.png`;
      link.href = url;
      link.click();
      toast.success("Squad card saved");
    } catch (err) {
      toast.error("Couldn't generate image — try again");
    } finally {
      setDownloading(false);
    }
  };

  const t = team;
  const pct = t?.budget ? Math.round(((t.budget - t.remaining_budget) / t.budget) * 100) : 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t?.name || "Team"}
      subtitle="SQUAD DETAIL"
      size="xl"
      testId="team-detail-modal"
    >
      {loading || !t ? (
        <div className="label-mono py-16 text-center">Loading team…</div>
      ) : (
        <div className="space-y-4" data-testid={`team-detail-${t.id}`}>
          <div className="flex justify-end">
            <button onClick={downloadPng} disabled={downloading} className="btn-ghost !px-3 !py-1.5 text-[11px] flex items-center gap-1.5" data-testid="download-squad-png">
              <DownloadSimple size={12} /> {downloading ? "Rendering…" : "Download PNG"}
            </button>
          </div>
          <div ref={cardRef} className="p-6 bg-white rounded" style={{ color: "#0F172A", fontFamily: "'Oswald', sans-serif" }}>
          <div className="flex items-center justify-between gap-4 mb-5">
            <div className="flex items-center gap-4">
              {t.logo_url ? (
                <img
                  src={resolveImageUrl(t.logo_url)}
                  alt=""
                  className="w-16 h-16 rounded object-cover border-2"
                  style={{ borderColor: t.color || "#0F172A" }}
                  onError={(e) => { e.target.style.display = "none"; }}
                  crossOrigin="anonymous"
                />
              ) : (
                <div
                  className="w-16 h-16 grid place-items-center rounded"
                  style={{ background: `${t.color}22`, border: `2px solid ${t.color}` }}
                >
                  <Trophy weight="fill" size={26} style={{ color: t.color }} />
                </div>
              )}
              <div>
                <div className="uppercase leading-none" style={{ fontWeight: 900, fontSize: 32, color: "#0F172A", letterSpacing: "-0.01em" }}>{t.name}</div>
                <div className="uppercase mt-1" style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.15em", fontSize: 11, color: "#475569", fontWeight: 600 }}>
                  {t.short_name} · OWNER {t.owner_name}
                </div>
              </div>
            </div>
            <img src={brandLogoUrl || "/nitisoft-logo.png"} alt="" className="h-10" style={{ opacity: 0.9 }} />
          </div>

          <div className="grid grid-cols-4 gap-3 mb-5">
            {[
              { label: "BUDGET",    value: t.budget,                       color: "#0F172A" },
              { label: "SPENT",     value: t.budget - t.remaining_budget,  color: "#0F172A" },
              { label: "REMAINING", value: t.remaining_budget,             color: "#059669" },
              { label: "PLAYERS",   value: `${t.players?.length || 0} / ${t.max_players}`, color: "#0F172A" },
            ].map((k) => (
              <div key={k.label} className="rounded p-3" style={{ background: "#F1F5F9", border: "1px solid #CBD5E1" }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.15em", fontSize: 10, color: "#475569", fontWeight: 700 }}>{k.label}</div>
                <div style={{ fontWeight: 900, fontSize: 26, color: k.color, lineHeight: 1, marginTop: 4 }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div className="h-1 rounded overflow-hidden mb-5" style={{ background: "#E2E8F0" }}>
            <div className="h-full" style={{ width: `${pct}%`, background: t.color }} />
          </div>

          <div>
            <div className="uppercase mb-3" style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.15em", fontSize: 11, color: "#475569", fontWeight: 700 }}>
              SQUAD ({t.players?.length || 0})
            </div>
            {(!t.players || t.players.length === 0) ? (
              <div className="rounded p-8 text-center" style={{ border: "1px dashed #CBD5E1", color: "#64748B" }}>
                No players purchased yet.
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2.5">
                {[...t.players]
                  .sort((a, b) => {
                    // Iter31: icon players first, then by player_number.
                    const ai = (a.is_icon || a.category === "Icon") ? 0 : 1;
                    const bi = (b.is_icon || b.category === "Icon") ? 0 : 1;
                    if (ai !== bi) return ai - bi;
                    return (a.player_number || 0) - (b.player_number || 0);
                  })
                  .map((p) => (
                  <div
                    key={p.id}
                    className="rounded overflow-hidden relative"
                    style={{ background: "#0F172A", border: (p.is_icon || p.category === "Icon") ? "2px solid #F59E0B" : "1px solid #1E293B" }}
                    data-testid={`team-detail-player-${p.id}`}
                  >
                    {p.player_number != null && (
                      <span
                        className="absolute top-1.5 left-1.5 z-10"
                        style={{ background: "#FBBF24", color: "#000", fontWeight: 900, fontSize: 11, padding: "1px 6px", borderRadius: 4, boxShadow: "0 1px 3px rgba(0,0,0,0.4)" }}
                        data-testid={`team-detail-player-num-${p.id}`}
                      >#{p.player_number}</span>
                    )}
                    {(p.is_icon || p.category === "Icon") && (
                      <span
                        className="absolute top-1.5 right-1.5 z-10"
                        style={{ background: "linear-gradient(135deg,#F59E0B,#D97706)", color: "#000", fontWeight: 900, fontSize: 9, padding: "2px 6px", borderRadius: 4, letterSpacing: "0.08em" }}
                        data-testid={`team-detail-icon-badge-${p.id}`}
                      >★ ICON</span>
                    )}
                    <div className="relative w-full overflow-hidden" style={{ aspectRatio: "4 / 5" }}>
                      <img
                        src={p.photo_url ? resolveImageUrl(p.photo_url) : "/player-placeholder.svg"}
                        alt={p.name}
                        className="absolute inset-0 w-full h-full"
                        style={{ objectFit: "cover", objectPosition: "center top" }}
                        onError={(e) => { e.target.src = "/player-placeholder.svg"; }}
                      />
                      <div className="absolute inset-x-0 bottom-0 p-2" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 20%, rgba(0,0,0,0.9) 100%)" }}>
                        <div className="uppercase" style={{ fontWeight: 900, fontSize: 13, color: "#fff", lineHeight: 1.1, textShadow: "0 2px 4px rgba(0,0,0,0.8)" }}>{p.name}</div>
                      </div>
                    </div>
                    <div className="px-2 py-1.5 flex items-center justify-between" style={{ background: "#F8FAFC" }}>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", fontSize: 9, color: "#64748B", fontWeight: 700 }}>{(p.is_icon || p.category === "Icon") ? "ICON" : "SOLD"}</span>
                      <span style={{ fontWeight: 900, fontSize: 15, color: "#059669", lineHeight: 1 }}>
                        {p.sold_price ?? 0}<span style={{ fontSize: 9, color: "#64748B", marginLeft: 2 }}>pts</span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
